import {
    useEffect,
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    type TracePlanStep,
    type TraceStatus,
    type ISource,
    ToggleGroup,
    Button,
    Trace,
    Text
} from "ncore-ui-kit";
import {
    InlineCode,
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";
import {
    SearchIcon
} from "../../anatomy/icons";

const NAME = "Trace";

const USAGE_CODE = `<Trace>
    <Trace.Reasoning
        text="The user asked for the October report."
        durationSeconds={12}
        status="done"
    />
    <Trace.ToolCall
        output={{
            total: 142
        }}
        input={{
            month: "2026-10"
        }}
        title="Report search"
        name="reports.search"
        icon={SearchIcon}
        duration={1400}
        status="done"
    />
    <Trace.Sources
        onSourcePress={openSource}
        sources={sources}
    />
</Trace>`;

const TOOL_CODE = `<Trace.ToolCall
    errorDescription="The service did not respond in time."
    errorTitle="The report search failed"
    onRetryPress={retry}
    onStopPress={stop}
    title="Report search"
    name="reports.search"
    status={status}
    input={input}
    output={output}
/>`;

const PLAN_CODE = `<Trace.Plan
    onStopPress={stop}
    title="Prepare the report"
    steps={[
        {
            title: "Read the data",
            status: "done"
        },
        {
            title: "Compare with the plan",
            status: "running"
        },
        {
            title: "Write the summary",
            status: "pending"
        }
    ]}
/>`;

const CUSTOM_CODE = `<Trace.Collapsible
    onOpenChange={setIsOpen}
    isOpen={isOpen}
>
    <Trace.Trigger>
        <Trace.Line
            title="Checked the permissions"
            status="done"
        />
    </Trace.Trigger>
    <Trace.Content>
        <Text>
            The user can read the reports folder.
        </Text>
    </Trace.Content>
</Trace.Collapsible>`;

const TOOL_STATUSES = [
    "running",
    "done",
    "error",
    "cancelled"
] as const;

const PLAN_KEYS = [
    "tracePage.stepRead",
    "tracePage.stepCompare",
    "tracePage.stepCharts",
    "tracePage.stepWrite"
] as const;

type ToolStatus = (typeof TOOL_STATUSES)[number];

const TOOL_INPUT = {
    month: "2026-10",
    status: [
        "open",
        "late"
    ]
};

const TOOL_OUTPUT = {
    late: [
        {
            task: "Payments",
            days: 12
        },
        {
            task: "Notifications",
            days: 6
        }
    ],
    owner: "Ada",
    total: 142,
    done: 118
};

const STAGE_DONE = 3;

const STAGE_DELAY = 1500;

const TOOL_RUN_DELAY = 2000;

const PLAN_DELAY = 1100;

const FULL_WIDTH_STYLE = {
    width: "100%"
} as const;

const createSources = (t: DocsTranslate): Array<ISource> => [
    {
        title: t("tracePage.sourceOne"),
        url: "https://example.com/q3",
        domain: "example.com"
    },
    {
        url: "https://example.com/sprint-14",
        title: t("tracePage.sourceTwo"),
        domain: "example.com"
    },
    {
        title: t("tracePage.sourceThree"),
        url: "https://example.com/budget",
        domain: "example.com"
    }
];

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("tracePage.reasoningTitle"),
        id: "reasoning"
    },
    {
        title: t("tracePage.toolsTitle"),
        id: "tools"
    },
    {
        title: t("tracePage.sourcesTitle"),
        id: "sources"
    },
    {
        title: t("tracePage.plansTitle"),
        id: "plans"
    },
    {
        title: t("tracePage.customTitle"),
        id: "custom"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const TracePage = () => {
    const {
        t
    } = useDocsI18n();

    const [
        stage,
        setStage
    ] = useState(STAGE_DONE);

    const [
        toolStatus,
        setToolStatus
    ] = useState<ToolStatus>("error");

    const [
        planStep,
        setPlanStep
    ] = useState<number>(PLAN_KEYS.length);

    const [
        isPlanStopped,
        setIsPlanStopped
    ] = useState(false);

    const [
        openedSource,
        setOpenedSource
    ] = useState<string | undefined>(undefined);

    const [
        isCustomOpen,
        setIsCustomOpen
    ] = useState(false);

    useEffect(() => {
        if(stage >= STAGE_DONE) {
            return;
        }

        const timer = window.setTimeout(() => setStage(stage + 1), STAGE_DELAY);

        return () => {
            window.clearTimeout(timer);
        };
    }, [
        stage
    ]);

    useEffect(() => {
        if(toolStatus !== "running") {
            return;
        }

        const timer = window.setTimeout(() => setToolStatus("done"), TOOL_RUN_DELAY);

        return () => {
            window.clearTimeout(timer);
        };
    }, [
        toolStatus
    ]);

    useEffect(() => {
        if(isPlanStopped || planStep >= PLAN_KEYS.length) {
            return;
        }

        const timer = window.setTimeout(() => setPlanStep(planStep + 1), PLAN_DELAY);

        return () => {
            window.clearTimeout(timer);
        };
    }, [
        isPlanStopped,
        planStep
    ]);

    const handleRun = () => setStage(0);

    const handleToolStatusChange = (value: string | Array<string>) => {
        const next = TOOL_STATUSES.find((item) => item === value);

        if(next) {
            setToolStatus(next);
        }
    };

    const handleToolStop = () => setToolStatus("cancelled");

    const handleToolRetry = () => setToolStatus("running");

    const handlePlanRun = () => {
        setIsPlanStopped(false);

        setPlanStep(0);
    };

    const handlePlanStop = () => setIsPlanStopped(true);

    const handleSourcePress = (source: ISource) => setOpenedSource(source.title);

    const sources = createSources(t);

    const getPlanStatus = (index: number): TraceStatus => {
        if(index < planStep) {
            return "done";
        }

        if(isPlanStopped) {
            return "cancelled";
        }

        return index === planStep ? "running" : "pending";
    };

    const planSteps: Array<TracePlanStep> = PLAN_KEYS.map((key, index) => ({
        status: getPlanStatus(index),
        title: t(key)
    }));

    return <Article>
        <ComponentHeader
            lead={t("tracePage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("tracePage.overviewText")}
            </Paragraph>
            <Paragraph>
                {t("tracePage.overviewAlternatives")}
            </Paragraph>
            <Preview
                caption={t("tracePage.overviewCaption")}
                isStacked={true}
            >
                <Trace
                    style={FULL_WIDTH_STYLE}
                >
                    <Trace.Reasoning
                        durationSeconds={stage === 0 ? undefined : 12}
                        status={stage === 0 ? "running" : "done"}
                        text={t("tracePage.reasoningText")}
                    />
                    {
                        stage >= 1 ?
                            <Trace.ToolCall
                                status={stage === 1 ? "running" : "done"}
                                duration={stage === 1 ? undefined : 1400}
                                description={t("tracePage.toolRecords")}
                                title={t("tracePage.toolTitle")}
                                name="reports.search"
                                output={TOOL_OUTPUT}
                                input={TOOL_INPUT}
                                icon={SearchIcon}
                            />
                        :
                            null
                    }
                    {
                        stage >= 2 ?
                            <Trace.Sources
                                status={stage === 2 ? "running" : "done"}
                                onSourcePress={handleSourcePress}
                                sources={sources}
                            />
                        :
                            null
                    }
                </Trace>
                <Button
                    isDisabled={stage < STAGE_DONE}
                    title={t("tracePage.run")}
                    onPress={handleRun}
                    variant="outline"
                    type="neutral"
                    size="small"
                />
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("tracePage.reasoningTitle")}
            id="reasoning"
        >
            <Paragraph>
                {t("tracePage.reasoningDescription")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <Trace
                    style={FULL_WIDTH_STYLE}
                >
                    <Trace.Reasoning
                        text={t("tracePage.reasoningText")}
                        isDefaultOpen={true}
                        durationSeconds={12}
                        status="done"
                    />
                    <Trace.Reasoning
                        text={t("tracePage.reasoningPartial")}
                        durationSeconds={6}
                        status="error"
                    />
                    <Trace.Reasoning
                        text={t("tracePage.reasoningPartial")}
                        durationSeconds={4}
                        status="cancelled"
                    />
                    <Trace.Reasoning
                        durationSeconds={4}
                        status="done"
                    />
                </Trace>
            </Preview>
        </Section>
        <Section
            title={t("tracePage.toolsTitle")}
            id="tools"
        >
            <Paragraph>
                {t("tracePage.toolsText")}
            </Paragraph>
            <Preview
                caption={t("tracePage.toolsCaption")}
                isStacked={true}
            >
                <ToggleGroup
                    onValueChange={handleToolStatusChange}
                    title={t("tracePage.toolStatus")}
                    attachBehaviour="attached"
                    isClearEnabled={false}
                    value={toolStatus}
                    variant="outline"
                    size="small"
                >
                    {
                        TOOL_STATUSES.map((item) => <ToggleGroup.Item
                            title={item}
                            value={item}
                            key={item}
                        />)
                    }
                </ToggleGroup>
                <Trace
                    style={FULL_WIDTH_STYLE}
                >
                    <Trace.ToolCall
                        duration={toolStatus === "done" ? 1400 : undefined}
                        errorDescription={t("tracePage.toolErrorText")}
                        errorTitle={t("tracePage.toolErrorTitle")}
                        description={t("tracePage.toolRecords")}
                        title={t("tracePage.toolTitle")}
                        onRetryPress={handleToolRetry}
                        onStopPress={handleToolStop}
                        name="reports.search"
                        output={TOOL_OUTPUT}
                        isDefaultOpen={true}
                        status={toolStatus}
                        input={TOOL_INPUT}
                        icon={SearchIcon}
                    />
                </Trace>
            </Preview>
            <CodeBlock
                code={TOOL_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("tracePage.sourcesTitle")}
            id="sources"
        >
            <Paragraph>
                {t("tracePage.sourcesText")}
            </Paragraph>
            <Preview
                caption={openedSource === undefined ? t("tracePage.sourcesHint") : t("tracePage.sourcesOpened", [
                    openedSource
                ])}
                isStacked={true}
            >
                <Trace
                    style={FULL_WIDTH_STYLE}
                >
                    <Trace.Sources
                        onSourcePress={handleSourcePress}
                        isDefaultOpen={true}
                        sources={sources}
                    />
                    <Trace.Sources
                        status="error"
                    />
                </Trace>
            </Preview>
        </Section>
        <Section
            title={t("tracePage.plansTitle")}
            id="plans"
        >
            <Paragraph>
                {t("tracePage.plansText")}
            </Paragraph>
            <Preview
                caption={t("tracePage.plansCaption")}
                isStacked={true}
            >
                <Trace
                    style={FULL_WIDTH_STYLE}
                >
                    <Trace.Plan
                        title={t("tracePage.planTitle")}
                        onStopPress={handlePlanStop}
                        steps={planSteps}
                    />
                </Trace>
                <Button
                    isDisabled={planStep < PLAN_KEYS.length && !isPlanStopped}
                    title={t("tracePage.runPlan")}
                    onPress={handlePlanRun}
                    variant="outline"
                    type="neutral"
                    size="small"
                />
            </Preview>
            <CodeBlock
                code={PLAN_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("tracePage.customTitle")}
            id="custom"
        >
            <Paragraph>
                {t("tracePage.customText")}
            </Paragraph>
            <Preview
                caption={t("tracePage.customCaption")}
                isStacked={true}
            >
                <Trace
                    style={FULL_WIDTH_STYLE}
                >
                    <Trace.Line
                        description={t("tracePage.lineDescription")}
                        title={t("tracePage.lineDone")}
                        icon={SearchIcon}
                        status="done"
                    />
                    <Trace.Line
                        title={t("tracePage.linePending")}
                        status="pending"
                    />
                    <Trace.Line
                        title={t("tracePage.lineError")}
                        status="error"
                    />
                    <Trace.Line
                        title={t("tracePage.lineCancelled")}
                        status="cancelled"
                    />
                    <Trace.Collapsible
                        onOpenChange={setIsCustomOpen}
                        isOpen={isCustomOpen}
                    >
                        <Trace.Trigger>
                            <Trace.Line
                                title={t("tracePage.permissionsTitle")}
                                status="done"
                            />
                        </Trace.Trigger>
                        <Trace.Content>
                            <Text
                                variant="bodyMediumSize"
                                color="mid"
                            >
                                {t("tracePage.permissionsText")}
                            </Text>
                        </Trace.Content>
                    </Trace.Collapsible>
                </Trace>
            </Preview>
            <CodeBlock
                code={CUSTOM_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("tracePage.a11yTrigger"),
                    t("tracePage.a11yKeyboard"),
                    t("tracePage.a11yContent"),
                    t("tracePage.a11yStatus"),
                    t("tracePage.a11yLive"),
                    t("tracePage.a11yCode"),
                    t("tracePage.a11ySources"),
                    t("tracePage.a11yMotion"),
                    t("tracePage.a11yParts")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <Paragraph>
                <InlineCode>
                    Trace
                </InlineCode>
                {" "}
                {t("tracePage.rootPropsText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("tracePage.rootChildren"),
                        type: "ReactNode",
                        name: "children"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("tracePage.rootCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("tracePage.rootCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph>
                <InlineCode>
                    Trace.Reasoning
                </InlineCode>
                {" "}
                {t("tracePage.reasoningPropsText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("tracePage.reasoningPropText"),
                        type: "string",
                        name: "text"
                    },
                    {
                        type: "\"running\" | \"done\" | \"error\" | \"cancelled\"",
                        description: t("tracePage.reasoningPropStatus"),
                        defaultValue: "\"running\"",
                        name: "status"
                    },
                    {
                        description: t("tracePage.reasoningPropDuration"),
                        name: "durationSeconds",
                        type: "number"
                    },
                    {
                        description: t("tracePage.reasoningPropOnStopPress"),
                        name: "onStopPress",
                        type: "() => void"
                    }
                ]}
            />
            <Paragraph>
                <InlineCode>
                    Trace.ToolCall
                </InlineCode>
                {" "}
                {t("tracePage.toolPropsText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("tracePage.toolPropTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        type: "\"running\" | \"done\" | \"error\" | \"cancelled\"",
                        description: t("tracePage.toolPropStatus"),
                        defaultValue: "\"running\"",
                        name: "status"
                    },
                    {
                        description: t("tracePage.toolPropName"),
                        type: "string",
                        name: "name"
                    },
                    {
                        description: t("tracePage.toolPropDescription"),
                        name: "description",
                        type: "string"
                    },
                    {
                        description: t("tracePage.toolPropDuration"),
                        name: "duration",
                        type: "number"
                    },
                    {
                        description: t("tracePage.toolPropInput"),
                        type: "Record<string, unknown>",
                        name: "input"
                    },
                    {
                        description: t("tracePage.toolPropOutput"),
                        type: "unknown",
                        name: "output"
                    },
                    {
                        description: t("tracePage.toolPropIcon"),
                        type: "NCoreUIKitIcon",
                        name: "icon"
                    },
                    {
                        description: t("tracePage.toolPropOnStopPress"),
                        name: "onStopPress",
                        type: "() => void"
                    },
                    {
                        description: t("tracePage.toolPropOnRetryPress"),
                        name: "onRetryPress",
                        type: "() => void"
                    },
                    {
                        description: t("tracePage.toolPropErrorTitle"),
                        name: "errorTitle",
                        type: "string"
                    },
                    {
                        description: t("tracePage.toolPropErrorDescription"),
                        name: "errorDescription",
                        type: "string"
                    }
                ]}
            />
            <Paragraph>
                <InlineCode>
                    Trace.Sources
                </InlineCode>
                {" "}
                {t("tracePage.sourcesPropsText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("tracePage.sourcesPropSources"),
                        type: "Array<ISource>",
                        defaultValue: "[]",
                        name: "sources"
                    },
                    {
                        description: t("tracePage.sourcesPropStatus"),
                        type: "\"running\" | \"done\" | \"error\"",
                        defaultValue: "\"done\"",
                        name: "status"
                    },
                    {
                        description: t("tracePage.sourcesPropTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("tracePage.sourcesPropDescription"),
                        name: "description",
                        type: "string"
                    },
                    {
                        description: t("tracePage.sourcesPropOnSourcePress"),
                        type: "(source: ISource, index: number) => void",
                        name: "onSourcePress"
                    }
                ]}
            />
            <Paragraph>
                <InlineCode>
                    Trace.Plan
                </InlineCode>
                {" "}
                {t("tracePage.planPropsText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("tracePage.planPropTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("tracePage.planPropSteps"),
                        type: "Array<TracePlanStep>",
                        defaultValue: "[]",
                        name: "steps"
                    },
                    {
                        description: t("tracePage.planPropOnStopPress"),
                        name: "onStopPress",
                        type: "() => void"
                    }
                ]}
            />
            <Paragraph>
                {t("tracePage.openPropsText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("tracePage.openIsOpen"),
                        name: "isOpen",
                        type: "boolean"
                    },
                    {
                        description: t("tracePage.openIsDefaultOpen"),
                        name: "isDefaultOpen",
                        type: "boolean"
                    },
                    {
                        description: t("tracePage.openOnOpenChange"),
                        type: "(isOpen: boolean) => void",
                        name: "onOpenChange"
                    }
                ]}
            />
            <Paragraph>
                <InlineCode>
                    Trace.Line
                </InlineCode>
                {" "}
                {t("tracePage.linePropsText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("tracePage.linePropTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        type: "\"pending\" | \"running\" | \"done\" | \"error\" | \"cancelled\"",
                        description: t("tracePage.linePropStatus"),
                        defaultValue: "\"done\"",
                        name: "status"
                    },
                    {
                        description: t("tracePage.linePropDescription"),
                        name: "description",
                        type: "string"
                    },
                    {
                        description: t("tracePage.linePropIcon"),
                        type: "NCoreUIKitIcon",
                        name: "icon"
                    },
                    {
                        description: t("tracePage.linePropIsShimmer"),
                        defaultValue: "false",
                        name: "isShimmer",
                        type: "boolean"
                    },
                    {
                        description: t("tracePage.linePropIsConnected"),
                        defaultValue: "false",
                        name: "isConnected",
                        type: "boolean"
                    }
                ]}
            />
            <Paragraph>
                <InlineCode>
                    Trace.Content
                </InlineCode>
                {" "}
                {t("tracePage.contentPropsText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("tracePage.contentIsGuideHidden"),
                        defaultValue: "false",
                        name: "isGuideHidden",
                        type: "boolean"
                    },
                    {
                        description: t("tracePage.contentIsKeepMounted"),
                        defaultValue: "false",
                        name: "isKeepMounted",
                        type: "boolean"
                    },
                    {
                        description: t("tracePage.contentContainerStyle"),
                        name: "contentContainerStyle",
                        type: "StyleProp<ViewStyle>"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("tracePage.partsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default TracePage;
